import { configureStore } from '@reduxjs/toolkit';

import * as reducers from './entity-slices';
import {appStateReducer, sessionStateReducer, uiStateReducer} from '@/Shared'


/**
 * =====================================================
 * Redux Store Configuration
 * =====================================================
 *
 * Central Redux store setup using @reduxjs/toolkit.
 *
 * Responsibilities:
 * - Combine domain entity reducers and global app reducers
 * - Expose typed RootState and AppDispatch for strong TypeScript support
 * - Provide AppThunk type for async actions
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */

/**
 * -----------------------------------------------------
 * Reducer composition
 * -----------------------------------------------------
 *
 * - Domain entity reducers are imported from `entity-slices`
 * - App-specific reducers for session, UI, and app-wide state
 *   are added explicitly
 *
 * This ensures a fully normalized, domain-oriented Redux store.
 */
export const store = configureStore({
	reducer: {

		// Global app state slices
		appState: appStateReducer,
		uiState : uiStateReducer,
		sessionState : sessionStateReducer,

		// Domain entity slices
		challenges: reducers.challengeReducer,
		contacts : reducers.contactReducer,
		users : reducers.userReducer,
		userSessions : reducers.userSessionReducer,
		needs : reducers.needReducer,
		countries : reducers.countryReducer,
		publications : reducers.publicationReducer,
		agentTargets : reducers.agentTargetReducer,
		properties : reducers.propertyReducer,
		mandates : reducers.mandateReducer,
		transactions : reducers.transactionReducer,
		leases : reducers.leaseReducer,
		matchings : reducers.matchingReducer,
		mediaFiles : reducers.mediaFileReducer,
		tasks : reducers.taskReducer,
		activities : reducers.activityReducer,
		plannings : reducers.planningReducer,
		birthWishes : reducers.birthWishReducer,
		documents : reducers.documentReducer,
		permissions : reducers.permissionReducer
	},
});


/**
 * -----------------------------------------------------
 * TypeScript typings
 * -----------------------------------------------------
 *
 * RootState   : type representing the entire Redux state tree
 * AppDispatch : type for dispatch function
 * AppThunk    : helper type for async Thunk actions
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export type AppThunk<ReturnType = void> = (
	dispatch: AppDispatch,
	getState: () => RootState
) => ReturnType;